// Pure helpers for archiving and restoring server-backed chat conversations.
//
// These are extracted from fullscreen-chat.tsx so they can be unit-tested
// without mounting the full React tree. The helpers never touch DOM globals
// or React state — they accept a fetch-compatible function and return a
// structured result that the caller uses to decide whether to mutate local
// state.
//
// KEY INVARIANTS (Task #79):
//   - We NEVER remove a conversation from local state unless the DELETE
//     response is successful (response.ok). fetch() does not throw on 4xx/5xx,
//     so every response must be checked explicitly.
//   - We NEVER re-insert archived conversations on Undo unless every POST
//     restore response is successful.
//   - For bulk operations: all-or-nothing — if any request fails we do not
//     pretend all succeeded.

/** Minimal fetch-compatible interface the helpers depend on (for testability). */
export type FetchFn = (url: string, init: RequestInit) => Promise<{ ok: boolean; status: number; text(): Promise<string> }>;

export interface ArchiveResult {
  /** true only if the server accepted the archive (response.ok). */
  success: boolean;
  /** Human-readable error message when success is false. */
  errorMessage?: string;
}

export interface BulkArchiveResult {
  /** IDs of conversations that were successfully archived on the server. */
  succeededIds: string[];
  /** IDs of conversations whose archive request failed. */
  failedIds: string[];
  /** Whether all requests succeeded. */
  allSucceeded: boolean;
  /** Human-readable error summary when allSucceeded is false. */
  errorMessage?: string;
}

export interface RestoreResult {
  /** true only if every restore request returned ok. */
  success: boolean;
  /** Human-readable error message when success is false. */
  errorMessage?: string;
}

/**
 * Archives a single server-backed conversation.
 * Returns {success:false} if the server returns a non-2xx response; the
 * caller MUST NOT remove the conversation from local state in that case.
 */
export async function archiveConversation(
  backendConversationId: string,
  token: string | null,
  fetchFn: FetchFn,
): Promise<ArchiveResult> {
  try {
    const res = await fetchFn(
      `/api/v1/my/conversations/${backendConversationId}`,
      {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      },
    );
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      const msg = extractErrorMessage(res.status, body);
      return { success: false, errorMessage: msg };
    }
    return { success: true };
  } catch (err: any) {
    // Network-level error (fetch itself threw — e.g. no connection).
    return { success: false, errorMessage: err?.message || "Network error — couldn't reach the server." };
  }
}

/**
 * Archives multiple server-backed conversations.
 * Returns per-conversation success/failure info. The caller MUST apply an
 * all-or-nothing mutation: only remove from local state when allSucceeded.
 */
export async function bulkArchiveConversations(
  backendConversationIds: string[],
  token: string | null,
  fetchFn: FetchFn,
): Promise<BulkArchiveResult> {
  if (backendConversationIds.length === 0) {
    return { succeededIds: [], failedIds: [], allSucceeded: true };
  }
  const results = await Promise.all(
    backendConversationIds.map(async (cid) => {
      const r = await archiveConversation(cid, token, fetchFn);
      return { cid, ...r };
    }),
  );
  const succeededIds = results.filter((r) => r.success).map((r) => r.cid);
  const failedIds = results.filter((r) => !r.success).map((r) => r.cid);
  const allSucceeded = failedIds.length === 0;
  const errorMessage = allSucceeded
    ? undefined
    : failedIds.length === 1
    ? results.find((r) => !r.success)?.errorMessage || "Failed to archive one conversation."
    : `Failed to archive ${failedIds.length} conversation${failedIds.length > 1 ? "s" : ""}. Please try again.`;
  return { succeededIds, failedIds, allSucceeded, errorMessage };
}

/**
 * Restores multiple server-backed conversations from the archive.
 * Returns {success:false} if ANY restore request fails. The caller MUST NOT
 * re-insert conversations into local state in that case.
 */
export async function restoreConversations(
  backendConversationIds: string[],
  token: string | null,
  fetchFn: FetchFn,
): Promise<RestoreResult> {
  if (backendConversationIds.length === 0) {
    return { success: true };
  }
  const results = await Promise.all(
    backendConversationIds.map(async (cid) => {
      try {
        const res = await fetchFn(
          `/api/v1/my/conversations/${cid}/restore`,
          {
            method: "POST",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          },
        );
        if (!res.ok) {
          const body = await res.text().catch(() => "");
          const msg = extractErrorMessage(res.status, body);
          return { success: false as const, errorMessage: msg };
        }
        return { success: true as const };
      } catch (err: any) {
        return {
          success: false as const,
          errorMessage: err?.message || "Network error — couldn't reach the server.",
        };
      }
    }),
  );
  const failures = results.filter((r) => !r.success);
  if (failures.length > 0) {
    const msg =
      failures.length === 1
        ? failures[0].errorMessage || "Restore failed."
        : `${failures.length} restore requests failed. Please try again.`;
    return { success: false, errorMessage: msg };
  }
  return { success: true };
}

// ──────────────────────────────────────────────────────────────────────────
// Internal helpers
// ──────────────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<number, string> = {
  401: "Your session has expired. Please sign in again.",
  403: "You don't have permission to perform this action.",
  404: "The conversation could not be found.",
  500: "Something went wrong on the server. Please try again.",
};

function extractErrorMessage(status: number, rawBody: string): string {
  const text = (rawBody || "").trim();
  if (text) {
    try {
      const j = JSON.parse(text);
      const msg = j?.message ?? j?.error ?? j?.detail ?? j?.title;
      if (typeof msg === "string" && msg.trim()) return msg.trim();
    } catch {
      if (!text.startsWith("<") && text.length <= 300) return text;
    }
  }
  return STATUS_LABELS[status] ?? `Request failed (${status}). Please try again.`;
}
